import { Check, Copy, Link2, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface EventSuccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    eventName: string;
    eventDate: string;
    eventStartTime?: string;
    eventEndTime?: string;
    eventVenue: string;
    eventId: string;
    onNavigateToMap?: () => void;
}

// Calcular duración entre dos horas en formato HH:mm
function calculateDuration(startTime?: string, endTime?: string): string | null {
    if (!startTime || !endTime) return null;

    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const diff = endMinutes - startMinutes;

    if (diff <= 0) return null;

    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}min`;
}

// Formatear hora (HH:mm:ss o HH:mm) a formato 12h (h:mmam/pm)
function formatTo12h(time?: string): string {
    if (!time) return '';
    const [h, m] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'pm' : 'am';
    const hours12 = h % 12 || 12;
    const minutes = m.toString().padStart(2, '0');
    return `${hours12}:${minutes}${ampm}`;
}

export function EventSuccessModal({
    isOpen,
    onClose,
    eventName,
    eventDate,
    eventStartTime,
    eventEndTime,
    eventVenue,
    eventId,
    onNavigateToMap,
}: EventSuccessModalProps) {
    const [copied, setCopied] = useState(false);
    const [showCheck, setShowCheck] = useState(false);

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // Cleanup al desmontar
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Animación del check al abrir
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setShowCheck(true), 200);
            return () => clearTimeout(timer);
        } else {
            setShowCheck(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const eventUrl = `${window.location.origin}/eventos/${eventId}`;
    const duration = calculateDuration(eventStartTime, eventEndTime);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(eventUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Error al copiar:', err);
        }
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: eventName,
                    text: `¡Te invito a ${eventName}!`,
                    url: eventUrl,
                });
            } catch (err) {
                // Usuario canceló o error
                console.log('Compartir cancelado');
            }
        } else {
            handleCopyLink();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-300">
                {/* Botón cerrar */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                >
                    <X className="w-5 h-5 text-slate-600" />
                </button>

                {/* Header con gradiente y círculo animado */}
                <div className="relative h-40 bg-gradient-to-b from-emerald-50 via-emerald-50/50 to-white flex items-center justify-center overflow-hidden">
                    {/* Círculos decorativos */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className={`absolute w-32 h-32 rounded-full border-2 border-emerald-200/40 transition-all duration-700 ${showCheck ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                        <div className={`absolute w-24 h-24 rounded-full border-2 border-emerald-300/50 transition-all duration-500 delay-100 ${showCheck ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                        <div className={`absolute w-16 h-16 rounded-full bg-emerald-100/60 transition-all duration-300 delay-150 ${showCheck ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                    </div>

                    {/* Círculo principal con check */}
                    <div className={`relative w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30 transition-all duration-500 ${showCheck ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
                        <Check className={`w-7 h-7 text-white transition-all duration-300 delay-300 ${showCheck ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} strokeWidth={3} />
                    </div>
                </div>

                {/* Contenido */}
                <div className="px-6 pb-6">
                    {/* Título */}
                    <h2 className="text-xl font-bold text-slate-900 text-center mb-2">
                        ¡Evento creado exitosamente!
                    </h2>

                    {/* Subtítulo */}
                    <p className="text-slate-500 text-center text-sm mb-5">
                        Ahora puedes configurar el mapa de asientos y compartir el evento con tus clientes.
                    </p>

                    {/* Resumen del evento */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
                        <blockquote className="text-sm text-slate-600 border-l-2 border-emerald-500 pl-3">
                            <p className="font-semibold text-slate-900 mb-1">{eventName}</p>
                            <p className="text-xs text-slate-500 mb-1">
                                {eventDate} • {eventVenue}
                            </p>
                            {eventStartTime && eventEndTime && (
                                <p className="text-xs text-slate-500">
                                    A las {formatTo12h(eventStartTime)} hasta las {formatTo12h(eventEndTime)}
                                    {duration && <span className="text-emerald-600 font-medium"> ({duration})</span>}
                                </p>
                            )}
                        </blockquote>
                    </div>

                    {/* Sección de compartir */}
                    <div className="flex items-center justify-center gap-3 py-3 border-t border-slate-100">
                        <button
                            onClick={handleShare}
                            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                        >
                            <Link2 className="w-4 h-4" />
                            <span>Compartir enlace</span>
                        </button>

                        <div className="w-px h-5 bg-slate-200" />

                        <button
                            onClick={handleCopyLink}
                            className={`flex items-center gap-2 text-sm font-medium transition-colors ${copied ? 'text-emerald-600' : 'text-emerald-500 hover:text-emerald-600'
                                }`}
                        >
                            {copied ? (
                                <>
                                    <Check className="w-4 h-4" />
                                    <span>¡Copiado!</span>
                                </>
                            ) : (
                                <>
                                    <Copy className="w-4 h-4" />
                                    <span>Copiar enlace</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Botón principal */}
                    <button
                        onClick={onNavigateToMap}
                        className="w-full mt-4 py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl transition-colors shadow-lg shadow-emerald-500/20"
                    >
                        Configurar mapa de asientos →
                    </button>
                </div>
            </div>
        </div>
    );
}
